// src/api/api.js
import axios from "axios";
import { API_BASE_URL, apiUrl } from "./config";
import Swal from "sweetalert2";

/* =========================================================
   AXIOS BASE INSTANCE
========================================================= */
const API = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 600000,
  headers: {
    Accept: "application/json",
  },
});

API.isCancel = axios.isCancel;

const getAuthToken = () => {
  if (typeof window === "undefined") return null;
  const token = window.sessionStorage.getItem("token");
  if (!token || token === "cookie-auth") return null;
  return token;
};

const hasAuthSession = () => {
  if (typeof window === "undefined") return false;
  return !!window.sessionStorage.getItem("token");
};

/* =========================================================
   REQUEST INTERCEPTOR (Attach Token)
========================================================= */
API.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token && token !== "null" && token !== "undefined") {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs (Public)
API.sendForgotPasswordOtp = (identifier) => API.post("/auth/forgot-password/otp", { identifier });
API.resetForgotPassword = (data) => API.post("/auth/forgot-password/reset", data);

// Admin ID Card Downloads
API.downloadAdminSingleId = async (playerId, eventId) => {
  try {
    const res = await API.get(
      `/admin/id-card/${playerId}/${eventId}`,
      { responseType: "blob" }
    );

    if (res.data.type === "application/json") {
      const text = await res.data.text();
      const errorData = JSON.parse(text);
      console.error("Backend Error Details:", errorData);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: errorData.message || "Unable to download ID card",
      });
      return;
    }

    const disposition = res.headers["content-disposition"] || res.headers["Content-Disposition"] || "";
    let filename = `ID_${playerId}_${eventId}.pdf`;
    const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(disposition);
    if (match?.[1]) filename = decodeURIComponent(match[1]);

    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Network or Auth Error:", err);
  }
};

API.downloadAdminBulkIds = async (eventId) => {
  try {
    const res = await API.get(
      `/admin/id-cards/bulk/${eventId}`,
      { responseType: "blob" }
    );

    if (res.data.type === "application/json") {
      const text = await res.data.text();
      const errorData = JSON.parse(text);
      console.error("Backend Error Details:", errorData);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: errorData.message || "Unable to download bulk ID cards",
      });
      return;
    }

    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ID_CARDS_EVENT_${eventId}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Network or Auth Error:", err);
  }
};

/* =========================================================
   RESPONSE INTERCEPTOR (Auth Handling)
========================================================= */
API.interceptors.response.use(
  (response) => {
    // If downloading a file (PDF / ZIP / image)
    if (
      response.config?.responseType === "blob" ||
      response.headers?.["content-type"]?.includes("application/pdf")
    ) {
      return response; // return FULL response
    }

    return response.data;
  },
  (error) => {
    if (
      axios.isCancel(error) ||
      error?.code === "ERR_CANCELED" ||
      error?.name === "CanceledError" ||
      String(error?.message || "").toLowerCase().includes("canceled")
    ) {
      return Promise.reject(error);
    }

    const res = error?.response;
    const config = error?.config;
    // Auto-retry transient network drops (e.g. ERR_NETWORK_CHANGED / socket resets / Wi-Fi drops)
    const retryCount = Number(config?._retryCount || 0);

    if (!res && config && retryCount < 2) {
      const newConfig = { ...config, _retryCount: retryCount + 1 };
      delete newConfig.signal; // Strip aborted controller signal so fresh request can proceed cleanly

      const delay = 350 * Math.pow(2, retryCount);
      return new Promise((resolve) => setTimeout(resolve, delay)).then(() => API.request(newConfig));
    }

    if (!res) {
      const isTimeout = error?.code === "ECONNABORTED";
      const isNetworkError =
        error?.code === "ERR_NETWORK" ||
        /network\s*error/i.test(String(error?.message || "")) ||
        /network\s*changed/i.test(String(error?.message || ""));

      const message = isTimeout
        ? "Request timed out. Please try again."
        : isNetworkError
        ? "No response received from server. Check backend URL/server status. For large uploads, refresh once to verify whether files were saved."
        : (error?.message || "Request failed before reaching server");
      const networkError = new Error(message);
      networkError.code = error?.code || "NO_RESPONSE";
      networkError.isNetworkDrop = true;
      networkError.cause = error;
      return Promise.reject(networkError);
    }

    if (res?.status === 401 && !config?.skipAuthRedirect) {
      const hadToken = hasAuthSession();

      if (hadToken) {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("role");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("role");

        if (!window.__redirecting) {
          window.__redirecting = true;
          setTimeout(() => {
            window.location.href = "/login";
          }, 100);
        }
      }
      const authPayload = res?.data && typeof res.data === "object" ? res.data : {};
      const authError = new Error(authPayload.message || "Unauthenticated");
      Object.assign(authError, authPayload, { status: 401, response: res });
      return Promise.reject(authError);
    }

    const data = res?.data;
    const payload = data && typeof data === "object" ? data : {};
    const fallback =
      res.status >= 500
        ? "Server error. Please try again shortly."
        : `Request failed (${res.status})`;
    let message =
      payload.message ||
      payload.error ||
      (typeof data === "string" && data.trim() ? data.trim() : "") ||
      fallback;

    if (res.status === 429) {
      const requestUrl = String(res.config?.url || "").toLowerCase();
      const retryAfter = Number(
        res.headers?.["retry-after"] ||
        res.headers?.["Retry-After"] ||
        0
      );

      if (requestUrl.includes("/auth/player-registration-otp/verify")) {
        message = retryAfter > 0
          ? `Too many OTP verification attempts. Please wait ${retryAfter} seconds and try again.`
          : "Too many OTP verification attempts. Please wait a moment and try again.";
      } else if (requestUrl.includes("/auth/player-registration-otp")) {
        message = retryAfter > 0
          ? `Too many OTP requests. Please wait ${retryAfter} seconds before requesting another OTP.`
          : "Too many OTP requests. Please wait before requesting another OTP.";
      } else if (message === "Too Many Attempts.") {
        message = "Too many requests. Please wait a moment and try again.";
      }
    }

    const apiError = new Error(message);
    Object.assign(apiError, payload, {
      status: res.status,
      response: res,
      message,
    });
    return Promise.reject(apiError);
  }
);

/* =========================================================
   UTILITIES
========================================================= */
async function downloadRegistrationCertificate(registrationId, { regenerate = false, convenorName = "", confirm_password = "", keep_signatures = false } = {}) {
  const params = new URLSearchParams();
  if (regenerate) params.set("regenerate", "1");
  if (keep_signatures) params.set("keep_signatures", "1");
  if (convenorName && String(convenorName).trim() !== "") {
    params.set("convenor_name", String(convenorName).trim());
  }
  if (confirm_password) {
    params.set("confirm_password", confirm_password);
  }
  const qs = params.toString();
  const url = `/admin/results/certificate/${registrationId}` + (qs ? `?${qs}` : "");

  const fullUrl = apiUrl(url);

  const token = getAuthToken();
  let resp;
  try {
    resp = await axios.get(fullUrl, {
      responseType: "blob",
      withCredentials: true,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: "application/json",
      },
    });
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data;
    let errMsg = "";

    if (data instanceof Blob) {
      try {
        const text = await data.text();
        try {
          const parsed = JSON.parse(text);
          errMsg = parsed?.message || parsed?.error;
        } catch (_jsonErr) {
          const matchMessage = /<div class="message"[^>]*>([\s\S]*?)<\/div>/i.exec(text);
          if (matchMessage?.[1]) {
            errMsg = matchMessage[1].trim();
          } else {
            const matchTitle = /<title>([\s\S]*?)<\/title>/i.exec(text);
            if (matchTitle?.[1]) {
              errMsg = matchTitle[1].trim();
            }
          }
        }
      } catch (_blobErr) {
        console.error("Blob read error:", _blobErr);
      }
    } else if (data && typeof data === "object") {
      errMsg = data.message || data.error;
    }

    if (!errMsg) {
      errMsg = err?.message || "Certificate download failed";
      if (status) errMsg += ` (${status})`;
    }

    throw new Error(errMsg);
  }

  const disposition =
    resp.headers["content-disposition"] ||
    resp.headers["Content-Disposition"] ||
    "";

  let filename = `certificate_${registrationId}.pdf`;
  const match =
    /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(disposition);

  if (match?.[1]) filename = decodeURIComponent(match[1]);

  return { blob: resp.data, filename };
}

async function downloadVolunteerRegistrationCertificate(
  registrationId,
  { regenerate = false, convenorName = "" } = {}
) {
  const params = new URLSearchParams();
  if (regenerate) params.set("regenerate", "1");
  if (convenorName && String(convenorName).trim() !== "") {
    params.set("convenor_name", String(convenorName).trim());
  }
  const qs = params.toString();
  const url = `/admin/volunteer-registrations/${registrationId}/certificate` + (qs ? `?${qs}` : "");

  const fullUrl = apiUrl(url);
  const token = getAuthToken();

  let resp;
  try {
    resp = await axios.get(fullUrl, {
      responseType: "blob",
      withCredentials: true,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: "application/json",
      },
    });
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data;
    let errMsg = "";

    if (data instanceof Blob) {
      try {
        const text = await data.text();
        try {
          const parsed = JSON.parse(text);
          errMsg = parsed?.message || parsed?.error;
        } catch (_jsonErr) {
          const matchMessage = /<div class="message"[^>]*>([\s\S]*?)<\/div>/i.exec(text);
          if (matchMessage?.[1]) {
            errMsg = matchMessage[1].trim();
          } else {
            const matchTitle = /<title>([\s\S]*?)<\/title>/i.exec(text);
            if (matchTitle?.[1]) {
              errMsg = matchTitle[1].trim();
            }
          }
        }
      } catch (_blobErr) {
        console.error("Blob read error:", _blobErr);
      }
    } else if (data && typeof data === "object") {
      errMsg = data.message || data.error;
    }

    if (!errMsg) {
      errMsg = err?.message || "Certificate download failed";
      if (status) errMsg += ` (${status})`;
    }

    throw new Error(errMsg);
  }

  const disposition =
    resp.headers["content-disposition"] ||
    resp.headers["Content-Disposition"] ||
    "";

  let filename = `volunteer_certificate_${registrationId}.pdf`;
  const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(disposition);
  if (match?.[1]) filename = decodeURIComponent(match[1]);

  return { blob: resp.data, filename };
}

/* =========================================================
   HOME & PUBLIC ENDPOINTS
========================================================= */
API.fetchSlider = () =>
  API.get("/home/slider").then((d) => d?.data || d || []);
API.fetchActivities = () => API.get("/home/activities");
API.fetchHomeImpact = () =>
  API.get("/home/impact").then((d) => d?.data || d || {});
API.fetchHomeMedalSummary = () =>
  API.get("/home/medal-summary").then((d) => d?.data || d || {});
API.fetchEvents = () => API.get("/events");
API.fetchCurrentLiveResults = () =>
  API.get("/live-results/current").then((d) => d?.data || d || {});
API.registerVolunteerForEvent = (eventId, payload) =>
  API.post(`/events/${eventId}/volunteer-register`, payload);
API.fetchGallery = () => API.get("/gallery");
API.fetchAboutUs = () => API.get("/aboutUs");
API.fetchAboutSections = (page = "about_us") =>
  API.get("/aboutUs/sections", { params: { page } }).then((d) => d?.data?.data || d?.data || d || []);
API.getSports = () =>
  API.get("/sports").then((d) => d?.data || d || []);
API.lookupPincode = (pincode) =>
  API.get(`/pincodes/${pincode}`).then((d) => d?.data || d || []);
API.checkPlayerMobile = (mobile, ignore = null) =>
  API.get("/auth/check-mobile", {
    params: { mobile, ...(ignore ? { ignore } : {}) },
  }).then((d) => d || {});
API.checkAdminUserMobile = (mobile, ignore = null) =>
  API.get("/admin/users/check-mobile", {
    params: { mobile, ...(ignore ? { ignore } : {}) },
  }).then((d) => d || {});

/* =========================================================
   AUTH & PROFILE
========================================================= */
API.fetchAuthProfile = () => API.get("/auth/profile");
API.fetchPlayerProfile = () => API.get("/player-profile");
API.fetchMyClassifications = () => API.get("/players/my-classifications");
API.updateProfile = (data) => API.put("/player-profile", data);
API.sendPasswordChangeOtp = () => API.post("/auth/password-change-otp");
API.changePassword = (data) => API.post("/change-password", data);
API.logout = () => API.post("/logout");

/* =========================================================
   PLAYER - GENERAL
========================================================= */
API.fetchPlayers = (params) =>
  API.get("/players", { params }).then((d) => d || []);

API.registerPlayer = (form) =>
  API.post("/register", form, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

API.sendPlayerRegistrationOtp = (email) =>
  API.post("/auth/player-registration-otp", { email });

API.verifyPlayerRegistrationOtp = (email, emailOtp) =>
  API.post("/auth/player-registration-otp/verify", {
    email,
    email_otp: emailOtp,
  });

/* =========================================================
   PLAYER - EVENT REGISTRATION FLOW
========================================================= */
API.fetchOpenEvents = () =>
  API.get("/players/events/open").then((d) => d?.data || d || []);

API.fetchPublicBankSettings = () =>
  API.get("/public/bank-settings").then((d) => d?.data || d || {});

API.fetchBankSettings = () =>
  API.get("/admin/bank-settings").then((d) => d?.data || d || {});

API.sendBankSettingsOtp = () =>
  API.post("/admin/bank-settings/send-otp");

API.updateBankSettings = (payload) =>
  API.put("/admin/bank-settings", payload);

API.fetchGamesBySport = (sportId, params = {}) =>
  API.get(`/events/${sportId}/games`, { params }).then((d) => {
    const list = d?.data || d || [];
    if (!Array.isArray(list)) return [];
    return list.map((row) => {
      const fallbackName =
        row?.game_name ||
        row?.name ||
        row?.title ||
        (row?.id ? `Game #${row.id}` : "");
      return {
        ...row,
        game_name: String(fallbackName || "").trim(),
      };
    });
  });

API.fetchPlayerClassification = (playerId, sportId) =>
  API.get(`/players/${playerId}/classification?sport_id=${sportId}`).then(
    (d) => d || {}
  );

API.registerEvent = (formData) =>
  API.post("/players/register-event", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((d) => d);

API.fetchPlayerRegistrations = (playerId) =>
  API.get(`/players/${playerId}/registrations`).then(
    (res) => res?.data || res || []
  );

API.fetchPlayerCertificates = () =>
  API.get("/players/my-certificates").then((d) => d?.data || d || []);

API.downloadPlayerCertificate = async (certificateId) => {
  const res = await API.get(`/players/my-certificates/${certificateId}/download`, {
    responseType: "blob",
  });

  const blob = res?.data;
  if (!blob) throw new Error("Empty certificate response");

  const disposition =
    res?.headers?.["content-disposition"] ||
    res?.headers?.["Content-Disposition"] ||
    "";

  let filename = `certificate_${certificateId}.pdf`;
  const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(disposition);
  if (match?.[1]) filename = decodeURIComponent(match[1]);

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return true;
};

// For downloading ID cards (player side)
API.downloadPlayerIdCard = async (eventId) => {
  try {
    const res = await API.get(`/player/id-card/${eventId}`, {
      responseType: "blob",
    });

    // Check if the response is JSON (which indicates an error from your controller)
    if (res.data.type === "application/json") {
      const text = await res.data.text();
      const errorData = JSON.parse(text);
      console.error("Backend Error Details:", errorData);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: errorData.message || "Unable to download ID card",
      });
      return;
    }

    const disposition = res.headers["content-disposition"] || res.headers["Content-Disposition"] || "";
    let filename = `ID_CARD_EVENT_${eventId}.pdf`;
    const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(disposition);
    if (match?.[1]) filename = decodeURIComponent(match[1]);

    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Network or Auth Error:", err);
  }
};

/* =========================================================
   ADMIN - EVENTS
========================================================= */
API.fetchAdminEvents = (params = {}) =>
  API.get("/admin/events", { params }).then((d) => d || []);

API.createEvent = (formData) =>
  API.post("/admin/events", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

API.updateEvent = (id, formData) => {
  formData.append("_method", "PUT");
  return API.post(`/admin/events/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

API.deleteEvent = (id) => API.delete(`/admin/events/${id}`);

API.deleteEventFile = (id, field) => API.delete(`/admin/events/${id}/file/${field}`);

API.fetchAgeCategorySettings = () =>
  API.get("/admin/age-category-settings").then((d) => d?.data || d || {});

API.updateAgeCategorySettings = (payload) =>
  API.put("/admin/age-category-settings", payload).then((d) => d?.data || d || {});


API.fetchEligibilityRules = () =>
  API.get("/admin/eligibility-rules").then((d) => d || {});

API.saveEligibilityRules = (rules) =>
  API.put("/admin/eligibility-rules", { rules }).then((d) => d || {});

API.resetEligibilityRules = () =>
  API.post("/admin/eligibility-rules/reset").then((d) => d || {});

API.fetchScoreSheetRules = () =>
  API.get("/admin/score-sheet-rules").then((d) => d || {});

API.fetchScoreSheetRuleOptions = () =>
  API.get("/admin/score-sheet-rules/options").then((d) => d || {});

API.createScoreSheetRule = (payload) =>
  API.post("/admin/score-sheet-rules", payload).then((d) => d || {});

API.updateScoreSheetRule = (id, payload) =>
  API.put(`/admin/score-sheet-rules/${id}`, payload).then((d) => d || {});

API.deleteScoreSheetRule = (id) =>
  API.delete(`/admin/score-sheet-rules/${id}`).then((d) => d || {});

/* =========================================================
   ADMIN - DISTRICT USERS
========================================================= */
API.fetchDistrictContacts = () =>
  API.get("/player/district-contacts").then((d) => d?.data || d || {});

API.updateDistrictInchargeAssignments = (districtId, managedDistrictIds) =>
  API.put(`/admin/users/districts/${districtId}/incharge-assignments`, {
    managed_district_ids: managedDistrictIds,
  });

API.fetchDistrictUsers = () =>
  API.get("/admin/users/district").then((d) => d?.data || d || []);

API.fetchDistrictOptions = () =>
  API.get("/admin/users/district-options").then((d) => d?.data || d || []);

API.updateDistrictLogo = (districtId, formData) =>
  API.post(`/admin/users/districts/${districtId}/logo`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

API.deleteDistrictLogo = (districtId) =>
  API.delete(`/admin/users/districts/${districtId}/logo`);

API.updateDistrictName = (districtId, name) =>
  API.put(`/admin/users/districts/${districtId}`, { name });

API.resetDistricts = () =>
  API.post("/admin/users/districts/reset");

API.fetchAdminUsers = (params = {}) =>
  API.get("/admin/users", { params }).then((d) => d?.data || d || []);

API.createAdminUser = (formData) =>
  API.post("/admin/users", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

API.updateAdminUser = (id, formData) => {
  formData.append("_method", "PUT");
  return API.post(`/admin/users/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

API.resetAdminUserPassword = (id, password) =>
  API.put(`/admin/users/${id}/reset-password`, { password });

API.updateDistrictUserBlockStatus = (id, is_blocked, blocked_reason = "") =>
  API.put(`/admin/users/${id}/block-status`, { is_blocked, blocked_reason });

API.deleteDistrictUser = (id) =>
  API.delete(`/admin/users/${id}`);

API.fetchOfficials = async (params = {}) => {
  const [officials, managers] = await Promise.all([
    API.get("/admin/users", { params: { ...params, role: "official", account_scope: "regular" } }),
    API.get("/admin/users", { params: { ...params, role: "manager", account_scope: "regular" } }),
  ]);
  const officialRows = officials?.data || officials || [];
  const managerRows = managers?.data || managers || [];
  return [...officialRows, ...managerRows].sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
  );
};

API.createOfficial = (formData) => {
  return API.post("/admin/users", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

API.updateOfficial = (id, formData) => {
  formData.append("_method", "PUT");
  return API.post(`/admin/users/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

API.deleteOfficial = (id) =>
  API.delete(`/admin/users/${id}`);

API.deleteAdminUserPhoto = (id) =>
  API.delete(`/admin/users/${id}/photo`);


API.createDistrictUser = (formData) =>
  API.post("/admin/users/district", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

API.resetPlayerPassword = (id, password) =>
  API.put(`/admin/players/${id}/reset-password`, { password });

API.updatePlayerBlockStatus = (id, is_blocked, blocked_reason = "") =>
  API.put(`/admin/players/${id}/block-status`, { is_blocked, blocked_reason });

API.updatePlayerFeeExemption = (id, is_fee_exempt) =>
  API.put(`/admin/players/${id}/fee-exemption`, { is_fee_exempt });

/* =========================================================
   ADMIN - MAIL CENTER
========================================================= */
API.fetchAdminMailSettings = () =>
  API.get("/admin/mail/settings").then((d) => d?.data || d || {});

API.updateAdminMailSettings = (payload) =>
  API.put("/admin/mail/settings", payload);

API.fetchOtpSettings = () =>
  API.get("/admin/otp/settings").then((d) => d?.data || d || {});

API.updateOtpSettings = (payload) =>
  API.put("/admin/otp/settings", payload);

API.fetchPublicOtpSettings = () =>
  API.get("/auth/otp-settings/public").then((d) => d?.data || d || {});

API.fetchAdminMailTemplates = () =>
  API.get("/admin/mail/templates").then((d) => d?.data || d || []);

API.updateAdminMailTemplate = (key, payload) =>
  API.put(`/admin/mail/templates/${key}`, payload);

API.resetAdminMailTemplate = (key) =>
  API.post(`/admin/mail/templates/${key}/reset`);

API.fetchAdminMailRecipients = (audience, params = {}) =>
  API.get("/admin/mail/recipients", { params: { audience, ...params } }).then(
    (d) => d?.data || d || []
  );

API.fetchExcellenceAssociationDistricts = () =>
  API.get("/excellence-awards/association-districts").then((d) => d?.data || d || []);

API.checkExcellenceAwardStatus = (mobile) =>
  API.get(`/excellence-awards/status?mobile=${mobile}`).then((d) => d?.data || d || {});

API.uploadExcellenceAdditionalDocs = (formData) =>
  API.post("/excellence-awards/upload-additional", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  }).then((d) => d?.data || d || {});

API.requestExcellenceAdditionalDocs = (id, payload) =>
  API.post(`/admin/excellence-awards/applications/${id}/request-docs`, payload).then((d) => d?.data || d || {});

API.sendAdminMail = (payload) =>
  API.post("/admin/mail/send", payload);

API.fetchOccasionPosters = () =>
  API.get("/admin/occasion-posters").then((d) => d?.data || d || []);

API.updateOccasionPoster = (id, formData) =>
  API.post(`/admin/occasion-posters/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

/* =========================================================
   ADMIN - REGISTRATIONS
========================================================= */
API.fetchEventRegistrations = (params) =>
  API.get("/admin/registrations", { params }).then((d) => d || []);

API.fetchEventRegistrationStats = () =>
  API.get("/admin/registrations/stats").then((d) => d || []);

API.sendRegistrationVenueDetails = (payload) =>
  API.post("/admin/registrations/send-venue-details", payload);

API.removeRegistrationGame = (registrationGameId) =>
  API.delete(`/admin/registrations/games/${registrationGameId}`);

API.fetchEventResults = (eventId) =>
  API.get(`/admin/registrations/event-results/${eventId}`).then((d) => d?.data || d || []);

API.fetchRegisteredPlayers = (eventId) =>
  API.get(`/admin/registrations/event-players/${eventId}`).then((d) => d?.data || d || []);

API.fetchAchievements = (params) =>
  API.get("/admin/registrations/achievements", { params }).then((d) => d?.data || d || []);

API.fetchEligibleRegistrationPlayers = (eventId) =>
  API.get("/admin/registrations/eligible-players", { params: { event_id: eventId } })
    .then((d) => d?.data || d || []);

API.selectPlayerForEvent = (payload) =>
  API.post("/admin/registrations/select-player", payload);

API.updateRegistrationStatus = (id, status) =>
  API.put(`/admin/registrations/${id}/status`, { status });

API.getClassificationCompletionStatus = (eventId) =>
  API.get(`/admin/game-engine/classification-status/${eventId}`);

API.changeRegistrationGame = (id, game_id) =>
  API.put(`/admin/registrations/${id}/change-game`, { game_id });

API.addRegistrationGame = (id, game_id) =>
  API.post(`/admin/registrations/${id}/games`, { game_id });

API.updateEscortDetails = (id, formData) =>
  API.put(`/admin/registrations/${id}/update-escort`, formData);

API.fetchVolunteerRegistrations = () =>
  API.get("/admin/volunteer-registrations").then((d) => d || []);

API.fetchVolunteerRegistrationStats = () =>
  API.get("/admin/volunteer-registrations/stats").then((d) => d || []);

API.updateVolunteerRegistrationStatus = (id, status, admin_note = "") =>
  API.put(`/admin/volunteer-registrations/${id}/status`, { status, admin_note });

API.downloadVolunteerCertificate = async (id, opts = {}) => {
  const { blob, filename } = await downloadVolunteerRegistrationCertificate(id, opts);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `volunteer_certificate_${id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return true;
};

/* =========================================================
   ADMIN - SLIDERS
========================================================= */
API.fetchSliders = () =>
  API.get("/admin/sliders").then((d) => d?.data || d || []);

API.addSlider = (formData) =>
  API.post("/admin/sliders", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
API.updateSlider = (id, formData) =>
  API.post(`/admin/sliders/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

API.toggleSlider = (id) => API.put(`/admin/sliders/${id}/toggle`);
API.deleteSlider = (id) => API.delete(`/admin/sliders/${id}`);
API.updateSliderOrder = (order) =>
  API.put("/admin/sliders/reorder", { order });

/* =========================================================
   ADMIN - RESULTS & CERTIFICATES
========================================================= */
API.fetchEventsByDate = (date) =>
  API.get("/admin/results/events-by-date", { params: { date } });

API.fetchEventPlayers = (eventId, params = {}) =>
  API.get(`/admin/results/event/${eventId}/players`, { params }).then((d) => d || []);

API.fetchCertificateResultsByEvent = (eventId, params = {}) =>
  API.fetchEventPlayers(eventId, params);

API.fetchResultEventContext = (eventId) =>
  API.get(`/admin/results/event/${eventId}/context`).then((d) => d || {});

API.fetchGeneratedScoreSheets = (params = {}) =>
  API.get("/admin/game-engine/generated-score-sheets", { params }).then((d) => d?.data || d || []);

API.saveTimedResults = (data) =>
  API.post("/admin/results/timed/save", data);

API.savePointsAttempts = (data) =>
  API.post("/admin/results/points/save-attempts", data);

API.computePositions = (data) =>
  API.post("/admin/results/compute-positions", data);
API.fetchLiveResultSettings = () =>
  API.get("/admin/live-results/settings").then((d) => d?.data || d || {});
API.updateLiveResultSettings = (payload) =>
  API.put("/admin/live-results/settings", payload);
API.fetchEventPublicationStatus = (eventId) =>
  API.get(`/admin/results/publication/${eventId}`).then((d) => d?.data || d || {});
API.updateEventPublicationStatus = (eventId, payload) =>
  API.put(`/admin/results/publication/${eventId}`, payload).then((d) => d?.data || d || {});

API.autoAdvance = (data) =>
  API.post("/admin/results/auto-advance", data);

API.generateFixtures = (data) =>
  API.post("/admin/results/fixtures/generate", data);

API.clearFixtures = (data) =>
  API.post("/admin/game-engine/fixtures/clear", data);

API.clearHeats = (data) =>
  API.post("/admin/game-engine/heats/clear", data);

API.listTeams = (eventId, params = {}) =>
  API.get(`/admin/results/event/${eventId}/teams`, { params }).then((d) => d || []);

API.createTeam = (eventId, payload) =>
  API.post(`/admin/results/event/${eventId}/teams/create`, payload);

API.addTeamMember = (teamId, payload) =>
  API.post(`/admin/results/teams/${teamId}/add-member`, payload);

API.listMatches = (eventId, params = {}) =>
  API.get(`/admin/results/event/${eventId}/matches`, { params }).then((d) => d || []);

API.createMatch = (eventId, payload) =>
  API.post(`/admin/results/event/${eventId}/matches/create`, payload);

API.saveMatchResult = (matchId, payload) =>
  API.post(`/admin/results/matches/${matchId}/save-result`, payload);

API.downloadRegistrationCertificate = (id, opts = {}) =>
  downloadRegistrationCertificate(id, opts);

API.downloadEventCertificatesZip = (eventId, opts = {}) => {
  const convenorName = String(opts?.convenorName || "").trim();
  return API.get(`/admin/results/certificates/bulk/${eventId}`, {
    params: convenorName ? { convenor_name: convenorName } : undefined,
    responseType: "blob",
  }).then((res) => {
    const blob = res?.data;
    if (!blob) throw new Error("Empty zip response");

    const disposition =
      res?.headers?.["content-disposition"] ||
      res?.headers?.["Content-Disposition"] ||
      "";

    let filename = `certificates_event_${eventId}.zip`;
    const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(disposition);
    if (match?.[1]) filename = decodeURIComponent(match[1]);

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  });
};

API.fetchIssuedCertificates = (params = {}) =>
  API.get("/admin/results/certificates-issued", { params }).then(
    (d) => d?.data || d || []
  );

API.revokeCertificate = (id, reason, confirm_password) =>
  API.post(`/admin/results/certificates/${id}/revoke`, { reason, confirm_password });

API.fetchCertificateLogos = () =>
  API.get("/admin/results/certificate-logos").then((d) => d?.data || d || []);

API.createCertificateLogo = (formData) =>
  API.post("/admin/results/certificate-logos", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

API.updateCertificateLogo = (id, formData) =>
  API.post(`/admin/results/certificate-logos/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

API.reorderCertificateLogos = (order = []) =>
  API.put("/admin/results/certificate-logos/reorder", { order });

API.deleteCertificateLogo = (id) =>
  API.delete(`/admin/results/certificate-logos/${id}`);

API.executeDatabaseQuery = (sql) =>
  API.post("/admin/database/execute-query", { sql });

API.executeDatabaseUpdate = (sql) =>
  API.post("/admin/database/execute-update", { sql });

API.verifyDatabaseOtp = (otp) =>
  API.post("/admin/database/verify-otp", { otp });

API.sendDatabaseQueryOtp = () =>
  API.post("/admin/database/send-otp");

/* =========================================================
   ADMIN - REPORTS
========================================================= */
API.fetchPlayersCount = (params = {}) => API.get("/admin/reports/players-count", { params });
API.fetchMedalsByLevel = (params = {}) => API.get("/admin/reports/medals-by-level", { params });
API.fetchMedalsYearly = (params = {}) => API.get("/admin/reports/medals-yearly", { params });
API.downloadReport = (report, format, params = {}) =>
  API.get(`/admin/reports/export/${report}/${format}`, {
    params,
    responseType: "blob",
  }).then((res) => {
    const blob = res?.data;
    if (!blob) throw new Error("Empty export response");

    const disposition =
      res?.headers?.["content-disposition"] ||
      res?.headers?.["Content-Disposition"] ||
      "";

    let filename = `${report}.${format === "excel" ? "xlsx" : "pdf"}`;
    const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(disposition);
    if (match?.[1]) filename = decodeURIComponent(match[1]);

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  });

API.downloadResultsReport = async (eventId, type, format = "pdf") => {
  let res;
  try {
    res = await API.get(`/admin/results/export/${type}/${eventId}/${format}`, {
      responseType: "blob",
    });
  } catch (err) {
    try {
      // Backward compatibility when server has only legacy route shape.
      res = await API.get(`/admin/results/export/${type}/${eventId}`, {
        params: { format },
        responseType: "blob",
      });
    } catch (fallbackErr) {
      throw fallbackErr || err;
    }
  }

  const blob = res?.data;
  if (!blob) throw new Error("Empty export response");

  const disposition =
    res?.headers?.["content-disposition"] ||
    res?.headers?.["Content-Disposition"] ||
    "";

  let filename = `${type}_results_${eventId}.${format === "excel" ? "xlsx" : "pdf"}`;
  const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(disposition);
  if (match?.[1]) filename = decodeURIComponent(match[1]);

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return true;
};

/* =========================================================
   ADMIN - ACHIEVEMENTS
========================================================= */
API.fetchPlayerAchievements = (playerId) =>
  API.get(`/admin/achievements/${playerId}`).then((d) => d || []);

API.addPlayerAchievement = (data) =>
  API.post("/admin/achievements", data);

API.updatePlayerAchievement = (id, data) =>
  API.put(`/admin/achievements/${id}`, data);

API.deletePlayerAchievement = (id) =>
  API.delete(`/admin/achievements/${id}`);

/* =========================================================
   ADMIN - PLAYERS & FILES
========================================================= */
API.fetchAdminPlayers = () => API.get("/admin/players");
API.fetchAllPlayersFull = (params = {}) =>
  API.get("/admin/all-players", { params }).then((d) => d || []);
API.fetchClassifiedPlayers = (params = {}) =>
  API.get("/admin/classifications/classified-players", { params }).then((d) => d || []);

API.deletePlayerFile = (id, field) =>
  API.delete(`/admin/players/${id}/file/${field}`);

/* =========================================================
   ADMIN - CONTENT SECTIONS
========================================================= */
API.fetchAdminContentSections = (page = "") => {
  const params = page ? { page } : undefined;
  return API.get("/admin/content-sections", { params }).then((d) => ({
    data: d?.data || [],
    pages: d?.pages || [],
  }));
};

API.createContentSection = (formData) =>
  API.post("/admin/content-sections", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

API.updateContentSection = (id, formData) => {
  formData.append("_method", "PUT");
  return API.post(`/admin/content-sections/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

API.reorderContentSections = (orders, page = "") =>
  API.put("/admin/content-sections/reorder", {
    page: page || undefined,
    orders: Array.isArray(orders) ? orders : [],
  });

API.deleteContentSection = (id) =>
  API.delete(`/admin/content-sections/${id}`);

/* =========================================================
   ADMIN - SIGNATURES
========================================================= */
API.fetchSignatures = (params = {}) =>
  API.get("/admin/signatures", { params }).then((d) => d?.data || d || []);

API.updateSignature = (formData) =>
  API.post("/admin/signatures", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

/* =========================================================
   ADMIN - NEWS CLIPS
========================================================= */
API.fetchNewsClips = () =>
  API.get("/news-clips").then((d) => d?.data || d || []);

API.fetchAdminNewsClips = () =>
  API.get("/admin/news-clips").then((d) => d?.data || d || []);

API.createNewsClip = (formData) =>
  API.post("/admin/news-clips", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

API.updateNewsClip = (id, formData) =>
  API.post(`/admin/news-clips/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

API.deleteNewsClip = (id) =>
  API.delete(`/admin/news-clips/${id}`);

/* =========================================================
   ADMIN - SOCIAL FEEDS
========================================================= */
API.fetchSocialFeeds = () =>
  API.get("/social-feeds").then((d) => d?.data || d || []);

API.fetchAdminSocialFeeds = () =>
  API.get("/admin/social-feeds").then((d) => d?.data || d || []);

API.createSocialFeed = (data) =>
  API.post("/admin/social-feeds", data);

API.updateSocialFeed = (id, data) =>
  API.post(`/admin/social-feeds/${id}`, data);

API.deleteSocialFeed = (id) =>
  API.delete(`/admin/social-feeds/${id}`);

API.updateSocialFeedOrder = (orders) =>
  API.put("/admin/social-feeds/reorder", { orders });

/* =========================================================
   ADMIN/DISTRICT PROFILE & PASSWORD
========================================================= */
API.fetchProfile = () => API.get("/auth/profile").then((d) => d?.data || d);
API.updateProfile = (data) => API.put("/auth/profile", data);
API.sendPasswordOtp = () => API.post("/auth/password-change-otp");
API.changePassword = (data) => API.post("/change-password", data);

/* =========================================================
   ADMIN - EXCELLENCE AWARDS RANKINGS & OTP
========================================================= */
API.fetchExcellenceRankings = (params) =>
  API.get("/admin/excellence-awards/rankings", { params });

API.sendExcellenceRankingsOtp = () =>
  API.post("/admin/excellence-awards/rankings/send-otp");

API.saveExcellenceRankings = (payload) =>
  API.post("/admin/excellence-awards/rankings/save", payload);

API.bulkRequestExcellenceDocs = (payload) =>
  API.post("/admin/excellence-awards/applications/bulk-request-docs", payload);

API.downloadExcellenceRankingsReport = async (format, params = {}) => {
  try {
    const res = await API.get(`/admin/excellence-awards/rankings/export/${format}`, {
      params,
      responseType: "blob",
    });

    if (res.data.type === "application/json") {
      const text = await res.data.text();
      const errorData = JSON.parse(text);
      console.error("Backend Error Details:", errorData);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: errorData.message || "Unable to download report",
      });
      return;
    }

    const disposition = res.headers["content-disposition"] || res.headers["Content-Disposition"] || "";
    let filename = `excellence_rankings.${format === 'excel' ? 'xlsx' : 'pdf'}`;
    const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(disposition);
    if (match?.[1]) filename = decodeURIComponent(match[1]);

    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Download Error:", err);
    Swal.fire("Error", "Failed to download rankings report", "error");
  }
};

/* =========================================================
   SYSTEM EMAIL LOGS & DELIVERY REPORTS
========================================================= */
API.mail = {
  getLogs: (params) => API.get("/admin/mail/logs", { params }),
  resend: (id) => API.post(`/admin/mail/logs/${id}/resend`),
  resendFailed: () => API.post("/admin/mail/logs/resend-failed"),
};

/* =========================================================
   COACHING CAMP MANAGEMENT API
========================================================= */
API.camp = {
  // Camps
  getCamps: () => API.get("/admin/camp/camps"),
  createCamp: (data) => API.post("/admin/camp/camps", data),
  getActiveCamp: () => API.get("/admin/camp/camps/active"),
  getCamp: (id) => API.get(`/admin/camp/camps/${id}`),
  updateCamp: (id, data) => API.put(`/admin/camp/camps/${id}`, data),
  setActiveCamp: (id) => API.put(`/admin/camp/camps/${id}/set-active`),
  deleteCamp: (id) => API.delete(`/admin/camp/camps/${id}`),

  // Coaches
  getCoaches: (params) => API.get("/admin/camp/coaches", { params }),
  getEligiblePlayers: (params) => API.get("/admin/camp/coaches/eligible-players", { params }),
  getPublicCoaches: (params) => API.get("/coaches/public", { params }),
  getPublicCoachProfile: (id) => API.get(`/coaches/public/${id}`),
  createCoach: (data) => API.post("/admin/camp/coaches", data),
  getCoach: (id) => API.get(`/admin/camp/coaches/${id}`),
  updateCoach: (id, data) => API.put(`/admin/camp/coaches/${id}`, data),
  toggleCoachStatus: (id) => API.put(`/admin/camp/coaches/${id}/toggle-status`),
  resetCoachPassword: (id, data) => API.put(`/admin/camp/coaches/${id}/reset-password`, data),
  deleteCoach: (id) => API.delete(`/admin/camp/coaches/${id}`),

  // Camp Officials
  getCampOfficials: (params) => API.get("/admin/camp/officials", { params }),
  getEligibleCampOfficials: (params) => API.get("/admin/camp/officials/eligible", { params }),
  enrollCampOfficial: (data) => API.post("/admin/camp/officials/enroll", data),
  getCampOfficial: (id) => API.get(`/admin/camp/officials/${id}`),
  createCampOfficial: (formData) => API.post("/admin/camp/officials", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  updateCampOfficial: (id, formData) => API.post(`/admin/camp/officials/${id}`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  deleteCampOfficial: (id) => API.delete(`/admin/camp/officials/${id}`),

  // Athletes
  getAthletes: (params) => API.get("/admin/camp/athletes", { params }),
  getAvailablePlayers: (params) => API.get("/admin/camp/athletes/available-players", { params }),
  enrollPlayer: (data) => API.post("/admin/camp/athletes/enroll-player", data),
  getNextBibNo: () => API.get("/admin/camp/athletes/next-bib"),
  createAthlete: (formData) => API.post("/admin/camp/athletes", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  getAthlete: (id, params) => API.get(`/admin/camp/athletes/${id}`, { params }),
  updateAthlete: (id, formData) => API.post(`/admin/camp/athletes/${id}`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  updateDisciplineCategory: (id, data) => API.post(`/admin/camp/athletes/${id}/discipline-category`, data),
  deleteAthleteClassification: (id, playerCategoryId) => API.delete(`/admin/camp/athletes/${id}/classification/${playerCategoryId}`),
  getEligibleAthleteGames: (id, params) => API.get(`/admin/camp/athletes/${id}/eligible-games`, { params }),
  allotSports: (id, data) => API.post(`/admin/camp/athletes/${id}/allot-sports`, data),
  updateIdNumber: (id, data) => API.post(`/admin/camp/athletes/${id}/id-number`, data),
  removeFromCamp: (id) => API.post(`/admin/camp/athletes/${id}/remove-from-camp`),
  readmitAthlete: (id) => API.post(`/admin/camp/athletes/${id}/readmit`),
  deleteAthlete: (id) => API.delete(`/admin/camp/athletes/${id}`),

  // Mappings
  getMappings: (params) => API.get("/admin/camp/mappings", { params }),
  createMapping: (data) => API.post("/admin/camp/mappings", data),
  addCoachToTeam: (data) => API.post("/admin/camp/mappings/add-coach", data),
  reassignMapping: (id, data) => API.put(`/admin/camp/mappings/${id}/reassign`, data),
  removeMapping: (id) => API.delete(`/admin/camp/mappings/${id}`),
  getCoachAthletes: (coachId, params) => API.get(`/admin/camp/mappings/coach/${coachId}`, { params }),

  // Attendance
  getAttendance: (params) => API.get("/admin/camp/attendance", { params }),
  getAttendanceSheet: (params) => API.get("/admin/camp/attendance/sheet", { params }),
  saveAttendance: (data) => API.post("/admin/camp/attendance/save", data),
  deleteAttendance: (id) => API.delete(`/admin/camp/attendance/${id}`),
  getCoachAttendanceSheet: (params) => API.get("/admin/camp/attendance/coaches/sheet", { params }),
  saveCoachAttendance: (data) => API.post("/admin/camp/attendance/coaches/save", data),
  getTodayStats: (params) => API.get("/admin/camp/attendance/today-stats", { params }),
  getAttendanceRangeReport: (params) => API.get("/admin/camp/attendance/range-report", { params }),
  uploadDailyAttendanceReport: (formData) => API.post("/admin/camp/attendance/upload-daily-report", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  getDailyAttendanceReports: (params) => API.get("/admin/camp/attendance/daily-reports", { params }),
  getPeopleAttendanceSheet: (params) => API.get("/camp/people-attendance/sheet", { params }),
  getPeopleAttendanceMonthlyReport: (params) => API.get("/camp/people-attendance/monthly-report", { params }),
  savePeopleAttendance: (data) => API.post("/camp/people-attendance/save", data),
  updatePeopleAttendanceResponsibilities: (data) => API.put("/camp/people-attendance/responsibilities", data),
  createAttendancePerson: (data) => API.post("/admin/camp/attendance/people", data),
  updateAttendancePerson: (id, data) => API.put(`/admin/camp/attendance/people/${id}`, data),
  deleteAttendancePerson: (id) => API.delete(`/admin/camp/attendance/people/${id}`),
  getCampManagers: (params) => API.get("/admin/camp/managers", { params }),
  getEligibleCampManagers: (params) => API.get("/admin/camp/managers/eligible", { params }),
  enrollCampManager: (data) => API.post("/admin/camp/managers/enroll", data),
  createCampManager: (data) => API.post("/admin/camp/managers", data),
  updateCampManager: (id, data) => API.put(`/admin/camp/managers/${id}`, data),
  deleteCampManager: (id, params) => API.delete(`/admin/camp/managers/${id}`, { params }),

  // Daily Individual Athlete Reports
  getAthleteDailyReports: (params) => API.get("/admin/camp/athlete-daily-reports", { params }),
  saveAthleteDailyReport: (formData) => API.post("/admin/camp/athlete-daily-reports", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  deleteAthleteDailyReport: (id) => API.delete(`/admin/camp/athlete-daily-reports/${id}`),

  // Report Cards
  getReportCards: (params) => API.get("/admin/camp/report-cards", { params }),
  saveReportCard: (data, config) => API.post("/admin/camp/report-cards", data, config),
  deleteReportCardFile: (data) => API.post("/admin/camp/report-cards/delete-file", data),
  getAllAthletesReportCards: (params) => API.get("/admin/camp/report-cards/all-athletes", { params }),
  getAthleteReportCard: (athleteId, params) => API.get(`/admin/camp/report-cards/athlete/${athleteId}`, { params }),

  // Weekly Reports
  getWeeklyReports: (params) => API.get("/admin/camp/weekly-reports", { params }),
  submitWeeklyReport: (formData) => API.post("/admin/camp/weekly-reports", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  deleteWeeklyReport: (id) => API.delete(`/admin/camp/weekly-reports/${id}`),

  // Performance Reports (Week 1 vs Week 9)
  getPerformanceReports: (params) => API.get("/admin/camp/performance-reports", { params }),
  savePerformanceReport: (data) => API.post("/admin/camp/performance-reports", data),
  getPerformanceComparison: (athleteId, params) => API.get(`/admin/camp/performance-reports/comparison/${athleteId}`, { params }),

  // Documents
  getDocuments: (params) => API.get("/admin/camp/documents", { params }),
  uploadDocument: (formData) => API.post("/admin/camp/documents", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  deleteDocument: (id) => API.delete(`/admin/camp/documents/${id}`),

  // Settings
  getSettings: () => API.get("/admin/camp/settings"),
  getClassifications: (params) => API.get("/admin/camp/classifications", { params }),
  updateSignatures: (data) => API.put("/admin/camp/settings/signatures", data),
  addDiscipline: (data) => API.post("/admin/camp/settings/disciplines", data),
  updateDiscipline: (id, data) => API.put(`/admin/camp/settings/disciplines/${id}`, data),
  deleteDiscipline: (id) => API.delete(`/admin/camp/settings/disciplines/${id}`),

  // Dashboard & Centralized Reports
  getAdminStats: (params) => API.get("/admin/camp/dashboard/stats", { params }),
  getCentralizedReports: (params) => API.get("/admin/camp/dashboard/centralized-reports", { params }),

  // Team Management (Point 1, 3)
  getTeams: (params) => {
    const p = typeof params === "object" ? params : params ? { camp_id: params } : {};
    return API.get("/admin/camp/teams", { params: p });
  },
  createTeam: (data) => API.post("/admin/camp/teams", data),
  updateTeamCoaches: (data) => API.put("/admin/camp/teams/coaches", data),
  updateTeamAthletes: (data) => API.put("/admin/camp/teams/athletes", data),
  disbandTeam: (data) => API.delete("/admin/camp/teams/disband", { data }),

  // Athlete Kits & Measurements (Point 18)
  getKits: (campIdOrParams, otherParams) => {
    let params = {};
    if (typeof campIdOrParams === "object") {
      params = { ...campIdOrParams };
    } else if (campIdOrParams) {
      params.camp_id = campIdOrParams;
    }
    if (otherParams && typeof otherParams === "object") {
      params = { ...params, ...otherParams };
    }
    return API.get("/admin/camp/kits", { params });
  },
  updateKitMeasurement: (athleteIdOrData, formData) => {
    let payload = {};
    if (typeof athleteIdOrData === "object") {
      payload = { ...athleteIdOrData };
    } else {
      payload = { athlete_id: athleteIdOrData, ...(formData || {}) };
    }
    return API.post("/admin/camp/kits/update", payload);
  },
  bulkUpdateKitMeasurements: (data) => {
    const rawList = Array.isArray(data)
      ? data
      : data?.measurements || data?.athletes || [];
    const measurements = rawList.map((item) => ({
      ...item,
      athlete_id: item.athlete_id || item.id,
      id: item.athlete_id || item.id,
    }));
    return API.post("/admin/camp/kits/bulk-update", { measurements, athletes: measurements });
  },

  // Room Incharges & Medical Care
  getRoomIncharges: (params) => API.get("/admin/camp/room-incharges", { params }),
  saveRoomIncharge: (data) => API.post("/admin/camp/room-incharges", data),
  updateRoomIncharge: (id, data) => API.put(`/admin/camp/room-incharges/${id}`, data),
  deleteRoomIncharge: (id) => API.delete(`/admin/camp/room-incharges/${id}`),
  sendMedicineMail: (roomNo, data) => API.post(`/admin/camp/room-incharges/send-medicine-mail/${encodeURIComponent(roomNo)}`, data),
  sendMedicineMailAll: (data) => API.post("/admin/camp/room-incharges/send-medicine-mail-all", data),
  triggerAutoMedicineReminders: (data) => API.post("/admin/camp/room-incharges/trigger-auto-reminders", data),

  // Asynchronous Background PDF Exports & Downloads Manager
  createExportJob: (data) => API.post("/admin/camp/exports/create", data),
  getExportJobs: (params) => API.get("/admin/camp/exports/jobs", { params }),
  getExportStatus: (id) => API.get(`/admin/camp/exports/status/${id}`),
  cancelExportJob: (id) => API.post(`/admin/camp/exports/${id}/cancel`),
  retryExportJob: (id) => API.post(`/admin/camp/exports/${id}/retry`),
  deleteExportJob: (id) => API.delete(`/admin/camp/exports/${id}`),
  // Use the same runtime-aware base URL as every other API call. Reading the
  // raw env value here sent localhost downloads to the production server.
  getExportDownloadUrl: (id) => `${API_BASE_URL}/camp/exports/download/${id}`,

  // Coach Portal APIs
  coachPortal: {
    getStats: (params) => API.get("/coach/portal/dashboard/stats", { params }),
    getActiveCamp: () => API.get("/coach/portal/camps/active"),
    getCamps: () => API.get("/coach/portal/camps"),
    getMyAthletes: (params) => API.get("/coach/portal/athletes", { params }),
    getAthlete: (id, params) => API.get(`/coach/portal/athletes/${id}`, { params }),
    allotSports: (id, data) => API.post(`/coach/portal/athletes/${id}/allot-sports`, data),
    // Kits & Caste Measurements
    getKits: (params) => API.get("/coach/portal/kits", { params }),
    updateKitMeasurement: (data) => API.post("/coach/portal/kits/update", data),
    bulkUpdateKitMeasurements: (data) => API.post("/coach/portal/kits/bulk-update", data),

    // Attendance & Scanned Sheet Upload
    getAttendanceSheet: (params) => API.get("/coach/portal/attendance/sheet", { params }),
    saveAttendance: (data) => API.post("/coach/portal/attendance/save", data),
    getAttendance: (params) => API.get("/coach/portal/attendance", { params }),
    getTodayStats: (params) => API.get("/coach/portal/attendance/today-stats", { params }),
    uploadDailyAttendanceReport: (formData) => API.post("/coach/portal/attendance/upload-daily-report", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    getDailyAttendanceReports: (params) => API.get("/coach/portal/attendance/daily-reports", { params }),

    // Daily Individual Athlete Reports
    getAthleteDailyReports: (params) => API.get("/coach/portal/athlete-daily-reports", { params }),
    saveAthleteDailyReport: (formData) => API.post("/coach/portal/athlete-daily-reports", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    deleteAthleteDailyReport: (id) => API.delete(`/coach/portal/athlete-daily-reports/${id}`),

    // Report Cards
    getReportCards: (params) => API.get("/coach/portal/report-cards", { params }),
    saveReportCard: (data, config) => API.post("/coach/portal/report-cards", data, config),
    deleteReportCardFile: (data) => API.post("/coach/portal/report-cards/delete-file", data),
    getAllAthletesReportCards: (params) => API.get("/coach/portal/report-cards/all-athletes", { params }),
    getAthleteReportCard: (athleteId, params) => API.get(`/coach/portal/report-cards/athlete/${athleteId}`, { params }),

    // Weekly Reports
    getWeeklyReports: (params) => API.get("/coach/portal/weekly-reports", { params }),
    submitWeeklyReport: (formData) => API.post("/coach/portal/weekly-reports", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    deleteWeeklyReport: (id) => API.delete(`/coach/portal/weekly-reports/${id}`),

    // Performance Reports (Week 1 & Week 9)
    getPerformanceReports: (params) => API.get("/coach/portal/performance-reports", { params }),
    savePerformanceReport: (data) => API.post("/coach/portal/performance-reports", data),
    uploadBenchmarkReport: (formData) => API.post("/coach/portal/performance-reports/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    getPerformanceComparison: (athleteId, params) => API.get(`/coach/portal/performance-reports/comparison/${athleteId}`, { params }),

    // Documents
    getDocuments: (params) => API.get("/coach/portal/documents", { params }),
    uploadDocument: (formData) => API.post("/coach/portal/documents", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    deleteDocument: (id) => API.delete(`/coach/portal/documents/${id}`),
    getMyRooms: (params) => API.get("/coach/portal/room-incharges/my-rooms", { params }),
  }
};

API.campOfficial = {
  getContext: () => API.get("/camp/official-portal/context"),
  getAttendanceRange: (params) => API.get("/camp/official-portal/attendance/range-report", { params }),
  getPeopleMonthly: (params) => API.get("/camp/official-portal/attendance/monthly-people", { params }),
  getDailyReports: (params) => API.get("/camp/official-portal/attendance/daily-reports", { params }),
  getDocuments: (params) => API.get("/camp/official-portal/documents", { params }),
  getWeeklyReports: (params) => API.get("/camp/official-portal/weekly-reports", { params }),
  getPerformanceReports: (params) => API.get("/camp/official-portal/performance-reports", { params }),
  getReportCards: (params) => API.get("/camp/official-portal/report-cards", { params }),
};

/* =========================================================
   EXPORT
========================================================= */
export default API;
